import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Megaphone, Upload, Calendar, Clock, Link, Type, 
  FileText, Eye, EyeOff, Save, Loader2, Sparkles,
  ArrowRight, X, Image as ImageIcon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PromoManagement = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    headline: "Don't Miss Out!",
    description: "Get your tickets now for our upcoming live event!",
    button_text: "Get Tickets",
    button_link: "/live",
    flyer_image_url: null,
    event_date: "",
    event_time: "",
    delay_seconds: 180
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/promo/admin/popup`);
      const data = await res.json();
      setSettings(prev => ({
        ...prev,
        ...data,
        event_date: data.event_date || "",
        event_time: data.event_time || ""
      }));
    } catch (err) {
      console.error('Error fetching promo settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    
    try {
      const res = await fetch(`${API}/api/promo/admin/popup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      console.error('Error saving promo settings:', err);
      setSaveMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/api/promo/admin/popup/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        setSettings(prev => ({ ...prev, flyer_image_url: data.image_url }));
      }
    } catch (err) {
      console.error('Error uploading image:', err);
    } finally {
      setUploading(false);
    }
  };

  const formatEventDate = (dateStr, timeStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      const formattedDate = date.toLocaleDateString('en-GB', options);
      if (timeStr) {
        return `${formattedDate} at ${timeStr}`;
      }
      return formattedDate;
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-red-500" />
            Cross-Platform Promotions
          </h2>
          <p className="text-gray-400 mt-1">
            Manage promotional popups that appear on VOD pages
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="border-white/20"
          >
            {showPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-red-600 hover:bg-red-500"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`p-4 rounded-lg ${
          saveMessage.type === 'success' 
            ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
            : 'bg-red-500/20 border border-red-500/30 text-red-400'
        }`}>
          {saveMessage.text}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Settings Form */}
        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Popup Status</h3>
                <p className="text-sm text-gray-400 mt-1">
                  {settings.enabled 
                    ? 'Popup is active and will show after 3 minutes' 
                    : 'Popup is disabled and won\'t appear'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={settings.enabled ? 'bg-green-500' : 'bg-gray-500'}>
                  {settings.enabled ? 'Active' : 'Inactive'}
                </Badge>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
            </div>
          </div>

          {/* Flyer Image Upload */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-purple-400" />
              Event Flyer
            </h3>
            
            {settings.flyer_image_url ? (
              <div className="relative aspect-video rounded-lg overflow-hidden mb-4 bg-gray-800">
                <img 
                  src={settings.flyer_image_url.startsWith('http') ? settings.flyer_image_url : `${API}${settings.flyer_image_url}`}
                  alt="Event Flyer"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSettings(prev => ({ ...prev, flyer_image_url: null }))}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="aspect-video rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center mb-4 bg-white/5">
                <div className="text-center">
                  <Upload className="h-10 w-10 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400">No image uploaded</p>
                </div>
              </div>
            )}
            
            <label className="block">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full border-white/20"
                disabled={uploading}
                asChild
              >
                <span className="cursor-pointer">
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {settings.flyer_image_url ? 'Change Image' : 'Upload Flyer Image'}
                </span>
              </Button>
            </label>
          </div>

          {/* Text Content */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Type className="h-5 w-5 text-blue-400" />
              Content
            </h3>
            
            <div>
              <label className="block text-sm text-gray-400 mb-2">Headline</label>
              <Input
                value={settings.headline}
                onChange={(e) => setSettings(prev => ({ ...prev, headline: e.target.value }))}
                placeholder="Don't Miss Out!"
                className="bg-white/10 border-white/20"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-2">Description</label>
              <Textarea
                value={settings.description}
                onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Get your tickets now for our upcoming live event!"
                className="bg-white/10 border-white/20 min-h-[80px]"
              />
            </div>
          </div>

          {/* Event Details */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-400" />
              Event Details
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Event Date</label>
                <Input
                  type="date"
                  value={settings.event_date}
                  onChange={(e) => setSettings(prev => ({ ...prev, event_date: e.target.value }))}
                  className="bg-white/10 border-white/20"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Event Time</label>
                <Input
                  type="time"
                  value={settings.event_time}
                  onChange={(e) => setSettings(prev => ({ ...prev, event_time: e.target.value }))}
                  className="bg-white/10 border-white/20"
                />
              </div>
            </div>
          </div>

          {/* Button Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10 space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Link className="h-5 w-5 text-yellow-400" />
              Call to Action
            </h3>
            
            <div>
              <label className="block text-sm text-gray-400 mb-2">Button Text</label>
              <Input
                value={settings.button_text}
                onChange={(e) => setSettings(prev => ({ ...prev, button_text: e.target.value }))}
                placeholder="Get Tickets"
                className="bg-white/10 border-white/20"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-2">Button Link</label>
              <Input
                value={settings.button_link}
                onChange={(e) => setSettings(prev => ({ ...prev, button_link: e.target.value }))}
                placeholder="/live or https://example.com/tickets"
                className="bg-white/10 border-white/20"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use relative paths (e.g., /live) for internal pages or full URLs for external links
              </p>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        {showPreview && (
          <div className="lg:sticky lg:top-4">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-purple-400" />
              Live Preview
            </h3>
            
            <div className="bg-gray-950 rounded-xl p-6 border border-white/10 min-h-[400px] relative">
              <p className="text-xs text-gray-500 mb-4 text-center">
                This is how the popup will appear in the bottom-right corner of the VOD page
              </p>
              
              {/* Preview Popup */}
              <div className="absolute bottom-4 right-4 max-w-sm w-full">
                <div className="bg-gradient-to-br from-gray-900 via-purple-950/50 to-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl shadow-purple-500/20 overflow-hidden">
                  {/* Close Button */}
                  <button className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-gray-400">
                    <X className="h-4 w-4" />
                  </button>
                  
                  {/* Flyer Image */}
                  {settings.flyer_image_url && (
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img 
                        src={settings.flyer_image_url.startsWith('http') ? settings.flyer_image_url : `${API}${settings.flyer_image_url}`}
                        alt="Event Flyer"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-1 bg-red-500/20 border border-red-500/30 rounded-full px-2 py-0.5">
                        <Sparkles className="h-3 w-3 text-red-400" />
                        <span className="text-xs font-medium text-red-400">Live Event</span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-1">
                      {settings.headline || 'Headline'}
                    </h3>
                    
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                      {settings.description || 'Description text will appear here'}
                    </p>
                    
                    {(settings.event_date || settings.event_time) && (
                      <div className="flex items-center gap-3 mb-4 text-sm">
                        <div className="flex items-center gap-1.5 text-purple-400">
                          <Calendar className="h-4 w-4" />
                          <span>{formatEventDate(settings.event_date, settings.event_time) || 'Event date'}</span>
                        </div>
                      </div>
                    )}
                    
                    <Button className="w-full bg-gradient-to-r from-red-600 to-pink-600 text-white font-semibold rounded-xl">
                      {settings.button_text || 'Button Text'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoManagement;
